/* eslint-disable no-case-declarations */
import moment from 'moment';

import { CopyData, ModalFilter, UnparsedDefaultFilters } from '../types';
import { store } from '../redux/store';

/**
 * Returns string with removed HTML tags
 * @param string
 * @returns string
 */
export const returnCleanedString = (string: string): string => {
  return string.replace(/(<([^>]+)>)/gi, '');
};

/**
 * Formats large number to proper decimal place and currency unit
 * @param n
 * @returns
 */
export const intToString = (
  n: number | undefined,
  places = 1,
  discardIndicator = false,
  currency: any = ''
): number | string | boolean | undefined => {
  if (n === 0) return n;
  if (!n) return undefined;
  if (n < 1e3) return currency === '¥' || currency === 'JPY' ? n.toFixed(0) : n.toFixed(2);
  if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(places) + `${discardIndicator ? `` : `K`}`;
  if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(places) + `${discardIndicator ? `` : `M`}`;
  if (n >= 1e9 && n < 1e12) return +(n / 1e9).toFixed(places) + `${discardIndicator ? `` : `B`}`;
  if (n >= 1e12) return +(n / 1e12).toFixed(1) + `${discardIndicator ? `` : `T`}`;
};

/**
 * Returns proper currency symbol for currencyCode argument ----- OBSOLETE
 * @param currencyCode
 * @returns
 */
export const returnCurrencySymbol = (currencyCode: string | null | undefined): string => {
  let symbol = '';
  const data = store.getState().data;

  if (data && data.currencies) {
    const currency = data.currencies.find(
      (currency: { currencyCode: string | null | undefined }) => currency.currencyCode === currencyCode
    );
    symbol = currency ? currency.currencySymbol : '';
  }

  return symbol;
};

export const returnCapitalizedString = (arg: string): string => {
  return arg.charAt(0).toUpperCase() + arg.slice(1);
};

export const formatDateFilter = (
  option: string,
  customRange: Array<number | string> = [-1, 0],
  copyData: CopyData,
  rangeLimit?: any
): string => {
  let startDate;
  let endDate;
  let formattedDate = '';

  let currentMonthAvailable;
  if (rangeLimit) currentMonthAvailable = moment(rangeLimit[0].startDate).format('MMM') === moment().format('MMM');

  switch (option) {
    case copyData?.dates_this_month:
      if (rangeLimit) {
        startDate = moment(rangeLimit[0].startDate).startOf('month').format('MMM DD, YYYY');
        endDate = moment(rangeLimit[0].startDate).endOf('month').format('MMM DD, YYYY');
      } else {
        startDate = moment().clone().startOf('month').format('MMM DD, YYYY');
        endDate = moment().clone().endOf('month').format('MMM DD, YYYY');
      }

      formattedDate = `${startDate} - ${endDate}`;
      break;
    case copyData?.dates_last_month:
      if (rangeLimit) {
        startDate = moment(rangeLimit[currentMonthAvailable ? 1 : 0].startDate)
          .startOf('month')
          .format('MMM DD, YYYY');
        endDate = moment(rangeLimit[currentMonthAvailable ? 1 : 0].startDate)
          .endOf('month')
          .format('MMM DD, YYYY');
      } else {
        startDate = moment().clone().subtract(1, 'months').startOf('month').format('MMM DD, YYYY');
        endDate = moment().clone().subtract(1, 'months').endOf('month').format('MMM DD, YYYY');
      }

      formattedDate = `${startDate} - ${endDate}`;
      break;
    case copyData?.dates_last_3_month:
      if (rangeLimit) {
        startDate = moment(rangeLimit[currentMonthAvailable ? 3 : 2].startDate)
          .startOf('month')
          .format('MMM DD, YYYY');
        endDate = moment(rangeLimit[currentMonthAvailable ? 1 : 0].startDate)
          .endOf('month')
          .format('MMM DD, YYYY');
      } else {
        startDate = moment().clone().subtract(3, 'months').startOf('month').format('MMM DD, YYYY');
        endDate = moment().clone().subtract(1, 'month').endOf('month').format('MMM DD, YYYY');
      }
      formattedDate = `${startDate} - ${endDate}`;
      break;
    case copyData?.dates_last_6_month:
      if (rangeLimit) {
        startDate = moment(rangeLimit[currentMonthAvailable ? 6 : 5].startDate)
          .startOf('month')
          .format('MMM DD, YYYY');
        endDate = moment(rangeLimit[currentMonthAvailable ? 1 : 0].startDate)
          .endOf('month')
          .format('MMM DD, YYYY');
      } else {
        startDate = moment().clone().subtract(6, 'months').startOf('month').format('MMM DD, YYYY');
        endDate = moment().clone().subtract(1, 'month').endOf('month').format('MMM DD, YYYY');
      }

      formattedDate = `${startDate} - ${endDate}`;
      break;
    case copyData?.dates_last_12_month:
      if (rangeLimit) {
        startDate = moment(rangeLimit[currentMonthAvailable ? 12 : 11].startDate)
          .startOf('month')
          .format('MMM DD, YYYY');
        endDate = moment(rangeLimit[currentMonthAvailable ? 1 : 0].startDate)
          .endOf('month')
          .format('MMM DD, YYYY');
      } else {
        startDate = moment().clone().subtract(12, 'months').startOf('month').format('MMM DD, YYYY');
        endDate = moment().clone().subtract(1, 'month').endOf('month').format('MMM DD, YYYY');
      }

      formattedDate = `${startDate} - ${endDate}`;
      break;
    case copyData?.dates_year_to_date:
      if (rangeLimit) {
        startDate = moment(rangeLimit[0].startDate).clone().startOf('year').format('MMM DD, YYYY');
        endDate = moment(rangeLimit[0].startDate).endOf('month').format('MMM DD, YYYY');
      } else {
        startDate = moment().clone().startOf('year').format('MMM DD, YYYY');
        endDate = moment().clone().endOf('month').format('MMM DD, YYYY');
      }

      formattedDate = `${startDate} - ${endDate}`;
      break;
    case copyData?.dates_custom_range:
      startDate = moment(customRange[0], 'MMM DD, YYYY').clone().startOf('month').format('MMM DD, YYYY');
      endDate = moment(customRange[1], 'MMM DD, YYYY').clone().endOf('month').format('MMM DD, YYYY');

      formattedDate = `${startDate} - ${endDate}`;
      break;
    default:
      startDate = moment().clone().subtract(3, 'months').startOf('month').format('MMM DD, YYYY');
      endDate = moment().clone().subtract(1, 'month').endOf('month').format('MMM DD, YYYY');

      formattedDate = `${startDate} - ${endDate}`;
      break;
  }

  return formattedDate;
};

/**
 * Returns filters in predictable format for API query consumption (refer to OpenAPI specs)
 * @param filters
 * @returns
 */
export const returnFormattedSegmentFilters = (filters: Array<ModalFilter | null>, includeShare?: boolean): any => {
  let formattedParams: any = {};

  // remove any null value filters
  const prunedFilters = filters.filter((filter) => {
    if (filter?.name === 'Share') {
      if (includeShare) return filter;
    } else {
      return filter;
    }
  });

  // if prune filters don't include Share, statically add all possible types (1P/2P/3P)
  if (!prunedFilters.some((filter) => filter?.name === 'Share') && includeShare) {
    prunedFilters.push({
      name: 'Share',
      values: [
        { id: '1P', name: '1P' },
        { id: '2P', name: '2P' },
        { id: '3P', name: '3P' }
      ]
    });
  }

  // loop through each valid filter
  prunedFilters.forEach((filter) => {
    const param = filter?.values?.map((value) => {
      // return encoded values for API purposes
      return encodeURIComponent(value.id).replace(/[!'()*]/g, (char) => {
        return '%' + char.charCodeAt(0).toString(16).toUpperCase();
      });
    });
    // append new object {filter.name}Ids: option1, option2, option3
    // refer to OpenAPI specs for format specifics
    formattedParams = {
      ...formattedParams,
      [`${
        filter?.name === 'Share' // TODO: clean this up a bit..
          ? 'sellerTypes'
          : filter?.name === 'SubCategory'
          ? 'subCategoryIds'
          : filter?.name.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase()) + 'Ids' // This regex is applying camelCase (requires ES6)
      }`]: param?.join(',')
    };
  });

  return formattedParams;
};

export const handleValueORPercentage = (
  value: number | string | undefined,
  valueType: string,
  selectedCurrency: string | null,
  places = 1
): string => {
  if (!value && value !== 0) return '--';
  //grab unit value type from graph data instead of from summary, in case they differ
  const isPercentage = valueType === 'percentage';
  const isCurrency = valueType === 'currency';
  const unit = isPercentage ? '%' : null;

  //create new number value for valid number testing, because type in Highcharts can be a string or number
  const vString = typeof value === 'undefined' ? '' : value.toString();
  const v: number = parseFloat(vString);

  if (value === Number.MAX_VALUE || !isFinite(v)) return '--';
  const valueString = value.toString();
  let withoutUnit: any = valueString;
  const isNegative = Number(value) < 0;

  if (!isPercentage) {
    (withoutUnit = unit
      ? value < 1
        ? valueString.substring(0, 3)
        : valueString.substring(0, 2)
      : intToString(Math.abs(Number(value)), places)),
      1,
      false,
      selectedCurrency;
  }

  const withUnit = unit
    ? `${withoutUnit}${unit}`
    : isCurrency
    ? `${returnCurrencySymbol(selectedCurrency)}${withoutUnit}`
    : `${withoutUnit}`;

  return isNegative && !isPercentage ? '-' + withUnit : withUnit;
};

export const parseDefaultFilters = (defaultFilters: UnparsedDefaultFilters): Array<ModalFilter | null> => {
  const getCampaignTypes = (retailers: any) => {
    const campaignTypes = retailers?.map((retailer: any) => {
      return retailer.campaignTypes?.map((ct: any) => {
        return {
          id: ct.id,
          name: ct.name
        };
      });
    });
    return campaignTypes;
  };

  const defaultMarketFilters: Array<ModalFilter | null> = [
    {
      name: 'Region',
      values: defaultFilters.regions?.map((region: any) => {
        return {
          id: region.id,
          name: region.name
        };
      })
    },
    {
      name: 'Retailer',
      values: defaultFilters.retailers?.map((retailer: any) => {
        return {
          id: retailer.id,
          name: retailer.name
        };
      })
    },
    {
      name: 'Market',
      values: defaultFilters.markets?.map((market: any) => {
        return {
          id: market.id,
          name: market.name
        };
      })
    },
    defaultFilters.channels?.length
      ? {
          name: 'Channel',
          values: defaultFilters.channels
        }
      : null,
    defaultFilters.categories?.length
      ? {
          name: 'Category',
          values: defaultFilters.categories
        }
      : null,
    defaultFilters.subCategories?.length
      ? {
          name: 'SubCategory',
          values: defaultFilters.subCategories
        }
      : null,
    defaultFilters.segments?.length
      ? {
          name: 'Segments',
          values: defaultFilters.segments
        }
      : null,
    defaultFilters.brands?.length
      ? {
          name: 'Brand',
          values: defaultFilters.brands
        }
      : null,
    {
      name: 'Share',
      values: defaultFilters.sellerTypes?.map((sellerType: any) => {
        return {
          id: sellerType,
          name: sellerType
        };
      })
    },
    {
      name: 'Campaign Type',
      values: defaultFilters.retailers?.some((retailer: any) => retailer.campaignTypes)
        ? getCampaignTypes(defaultFilters.retailers)
        : null
    }
  ];

  return defaultMarketFilters;
};
